// Venue-local event time helpers.
//
// Reach event dates arrive as a "YYYY-MM-DD HH:mm:ss" (or date-only) wall-clock
// string in the venue's own timezone. To decide whether an event has passed —
// or how long remains until it starts — we must interpret that wall-clock time
// in the venue's IANA timezone (resolved server-side and exposed as
// `eventTimezone`), not in the browser's timezone or as a naive UTC date.

interface VenueDateParts {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
  s: number;
  hasTime: boolean;
}

// Pull the date (and optional time) components out of the raw event string.
// When no time portion is present we treat the moment as end-of-day (23:59:59)
// so the event stays "upcoming" for the whole of its calendar day.
export function parseVenueDate(s?: string | null): VenueDateParts | null {
  if (!s) return null;
  const dm = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!dm) return null;
  const tm = s.match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
  return {
    y: Number(dm[1]),
    mo: Number(dm[2]),
    d: Number(dm[3]),
    h: tm ? Number(tm[1]) : 23,
    mi: tm ? Number(tm[2]) : 59,
    s: tm ? (tm[3] ? Number(tm[3]) : 0) : 59,
    hasTime: !!tm,
  };
}

// Offset (in ms) to add to a UTC instant to get the wall-clock time in `tz` at
// that instant. Computed via Intl so it correctly accounts for DST. Returns 0
// (i.e. treat as UTC) for an unknown/invalid timezone.
function tzOffsetMs(tz: string, at: Date): number {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const m: Record<string, number> = {};
    for (const p of dtf.formatToParts(at)) {
      if (p.type !== "literal") m[p.type] = Number(p.value);
    }
    const asUTC = Date.UTC(m.year, m.month - 1, m.day, m.hour, m.minute, m.second);
    return asUTC - at.getTime();
  } catch {
    return 0;
  }
}

// Convert a venue-local wall-clock event date into the absolute UTC instant it
// represents. Returns null when there is no parseable date.
export function venueDateToInstant(
  eventDate?: string | null,
  tz?: string | null,
): Date | null {
  const p = parseVenueDate(eventDate);
  if (!p) return null;
  const zone = tz || "UTC";
  // Treat the wall-clock components as if they were UTC, then correct by the
  // timezone's offset at (approximately) that instant.
  const guessUTC = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
  const offset = tzOffsetMs(zone, new Date(guessUTC));
  return new Date(guessUTC - offset);
}

// True when the event's venue-local datetime is in the past. Events with no
// parseable date are never considered past (they stay in "upcoming").
export function isEventPast(
  eventDate?: string | null,
  tz?: string | null,
  now: Date = new Date(),
): boolean {
  const inst = venueDateToInstant(eventDate, tz);
  if (!inst) return false;
  return inst.getTime() < now.getTime();
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// The venue-local clock time of an event as a 12-hour string, e.g. "8:00 PM".
// Returns null when the event has no time portion (date-only) so callers can
// fall back to showing the date alone.
export function formatVenueClockTime(eventDate?: string | null): string | null {
  const p = parseVenueDate(eventDate);
  if (!p || !p.hasTime) return null;
  const h12 = ((p.h + 11) % 12) + 1;
  const ampm = p.h < 12 ? "AM" : "PM";
  return `${h12}:${String(p.mi).padStart(2, "0")} ${ampm}`;
}

// Short timezone abbreviation for the venue at the event's instant, e.g. "EDT",
// "PST", "GMT+1". DST-aware (resolved at the event's actual instant). Returns
// null when there is no parseable date.
export function venueTimezoneAbbrev(
  eventDate?: string | null,
  tz?: string | null,
): string | null {
  const inst = venueDateToInstant(eventDate, tz);
  if (!inst) return null;
  const zone = tz || "UTC";
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "short",
    });
    const part = dtf.formatToParts(inst).find((p) => p.type === "timeZoneName");
    return part?.value ?? null;
  } catch {
    return null;
  }
}

// Venue-local start time with its timezone abbreviation, e.g. "8:00 PM EDT".
// The trailing abbreviation makes it unambiguous that the time is the venue's
// own local time, not the viewer's browser timezone. Returns null for date-only
// events (no time portion) so callers show the date alone.
export function formatVenueTimeLabel(
  eventDate?: string | null,
  tz?: string | null,
): string | null {
  const clock = formatVenueClockTime(eventDate);
  if (!clock) return null;
  const abbrev = venueTimezoneAbbrev(eventDate, tz);
  return abbrev ? `${clock} ${abbrev}` : clock;
}

// Full venue-local date (and time when present), e.g. "Jun 12, 8:00 PM EDT" or
// just "Jun 12" for a date-only event. Returns null when there is no parseable
// date. Used for the workspace header.
export function formatVenueDateTime(
  eventDate?: string | null,
  tz?: string | null,
): string | null {
  const p = parseVenueDate(eventDate);
  if (!p) return null;
  const date = `${MONTHS[p.mo - 1]} ${p.d}`;
  const timeLabel = formatVenueTimeLabel(eventDate, tz);
  return timeLabel ? `${date}, ${timeLabel}` : date;
}

// Compact, human-readable time until (or since) the event starts, in venue-local
// terms: "in 3 days, 4 hrs", "in 45 min", "started 2 hrs ago". Not a live
// ticker — computed once per render. Returns null when there is no date.
export function formatTimeRemaining(
  eventDate?: string | null,
  tz?: string | null,
  now: Date = new Date(),
): string | null {
  const inst = venueDateToInstant(eventDate, tz);
  if (!inst) return null;

  const diff = inst.getTime() - now.getTime();
  const future = diff >= 0;
  const mins = Math.round(Math.abs(diff) / 60000);
  if (mins < 1) return future ? "starting now" : "just started";

  const totalHrs = Math.floor(mins / 60);
  const days = Math.floor(totalHrs / 24);
  const hrs = totalHrs % 24;
  const remMin = mins % 60;

  let body: string;
  if (days > 0) {
    body = `${days} day${days === 1 ? "" : "s"}`;
    if (hrs > 0) body += `, ${hrs} hr${hrs === 1 ? "" : "s"}`;
  } else if (totalHrs > 0) {
    body = `${totalHrs} hr${totalHrs === 1 ? "" : "s"}`;
    if (remMin > 0) body += `, ${remMin} min`;
  } else {
    body = `${mins} min`;
  }

  return future ? `in ${body}` : `started ${body} ago`;
}
